import logger from '../utils/logger';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Target, Plus, TrendingUp, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';

const STATUS_COLORS = {
    not_started: 'bg-slate-100 text-slate-600',
    in_progress: 'bg-blue-100 text-blue-700',
    at_risk: 'bg-amber-100 text-amber-700',
    completed: 'bg-emerald-100 text-emerald-700',
    abandoned: 'bg-red-100 text-red-700',
};

const Goals = () => {
    const { user } = useAuth();
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [checkinOpen, setCheckinOpen] = useState(null);
    const [form, setForm] = useState({
        title: '', description: '', quarter: `Q${Math.ceil((new Date().getMonth() + 1) / 3)}-${new Date().getFullYear()}`,
        category: 'performance', weight: 20, key_results: [],
    });
    const [krDraft, setKrDraft] = useState({ title: '', target: 0, unit: '' });
    const [checkinForm, setCheckinForm] = useState({ progress_pct: 0, note: '', confidence: 'on_track' });

    const fetchGoals = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/goals`, { withCredentials: true });
            setGoals(data || []);
        } catch (e) { logger.error('goals fetch', e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchGoals(); }, [fetchGoals]);

    const submitGoal = async () => {
        if (!form.title || !form.quarter) { alert('Title and quarter required'); return; }
        try {
            await axios.post(`${API_URL}/api/goals`, {
                ...form,
                employee_id: user._id,
                key_results: form.key_results.map(kr => ({ ...kr, target: parseFloat(kr.target) || 0, actual: 0 })),
            }, { withCredentials: true });
            setForm({ title: '', description: '', quarter: form.quarter, category: 'performance', weight: 20, key_results: [] });
            setCreateOpen(false);
            fetchGoals();
        } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    };

    const submitCheckin = async () => {
        try {
            await axios.post(`${API_URL}/api/goals/${checkinOpen}/checkin`, checkinForm, { withCredentials: true });
            setCheckinOpen(null);
            setCheckinForm({ progress_pct: 0, note: '', confidence: 'on_track' });
            fetchGoals();
        } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    };

    const addKR = () => {
        if (!krDraft.title || !krDraft.target) return;
        setForm({ ...form, key_results: [...form.key_results, krDraft] });
        setKrDraft({ title: '', target: 0, unit: '' });
    };

    const completed = goals.filter(g => g.status === 'completed').length;
    const inProgress = goals.filter(g => g.status === 'in_progress').length;
    const atRisk = goals.filter(g => g.status === 'at_risk').length;
    const avgProgress = goals.length ? Math.round(goals.reduce((s, g) => s + (g.progress_pct || 0), 0) / goals.length) : 0;

    return (
        <div className="space-y-6" data-testid="goals-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Goals & OKRs</h1>
                    <p className="text-slate-500 mt-1">Quarterly objectives, weekly check-ins, key results — own your trajectory.</p>
                </div>
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="new-goal-btn"><Plus className="h-4 w-4 mr-2" />New Goal</Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Create Goal / OKR</DialogTitle><DialogDescription>Add a quarterly objective with measurable key results.</DialogDescription></DialogHeader>
                        <div className="space-y-3 mt-4">
                            <div><Label className="text-xs font-bold uppercase text-slate-500">Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="goal-title-input" /></div>
                            <div><Label className="text-xs font-bold uppercase text-slate-500">Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" rows={2} /></div>
                            <div className="grid grid-cols-3 gap-3">
                                <div><Label className="text-xs font-bold uppercase text-slate-500">Quarter</Label><Input value={form.quarter} onChange={e => setForm({ ...form, quarter: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" placeholder="Q1-2026" /></div>
                                <div><Label className="text-xs font-bold uppercase text-slate-500">Category</Label>
                                    <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm mt-1">
                                        {['performance', 'learning', 'team', 'personal'].map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div><Label className="text-xs font-bold uppercase text-slate-500">Weight %</Label><Input type="number" min="1" max="100" value={form.weight} onChange={e => setForm({ ...form, weight: parseInt(e.target.value) })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" /></div>
                            </div>

                            {/* Key Results builder */}
                            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
                                <Label className="text-xs font-bold uppercase text-slate-500">Key Results</Label>
                                {form.key_results.map((kr, i) => (
                                    <div key={`${kr.title}-${kr.target}-${i}`} className="text-xs flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2 mt-2">
                                        <span><strong>{kr.title}</strong> · target {kr.target} {kr.unit}</span>
                                        <button onClick={() => setForm({ ...form, key_results: form.key_results.filter((_, j) => j !== i) })} className="text-red-500 hover:text-red-700">×</button>
                                    </div>
                                ))}
                                <div className="flex gap-2 mt-2">
                                    <Input placeholder="KR title (e.g., 'Disburse 100 loans')" value={krDraft.title} onChange={e => setKrDraft({ ...krDraft, title: e.target.value })} className="flex-1 rounded-lg bg-white text-xs" />
                                    <Input type="number" placeholder="Target" value={krDraft.target} onChange={e => setKrDraft({ ...krDraft, target: e.target.value })} className="w-24 rounded-lg bg-white text-xs" />
                                    <Input placeholder="Unit" value={krDraft.unit} onChange={e => setKrDraft({ ...krDraft, unit: e.target.value })} className="w-20 rounded-lg bg-white text-xs" />
                                    <Button variant="outline" className="rounded-lg" onClick={addKR}>+</Button>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2"><Button variant="outline" className="rounded-lg" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={submitGoal} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="goal-save-btn">Create Goal</Button></div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Summary KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { label: 'Total Goals', value: goals.length, color: 'border-t-[#2563eb]', textColor: 'text-slate-900' },
                    { label: 'In Progress', value: inProgress, color: 'border-t-blue-500', textColor: 'text-blue-700' },
                    { label: 'At Risk', value: atRisk, color: 'border-t-amber-500', textColor: 'text-amber-600' },
                    { label: 'Completed', value: completed, color: 'border-t-emerald-500', textColor: 'text-emerald-600' },
                ].map(m => (
                    <Card key={m.label} className={`bg-white border border-slate-200 rounded-xl shadow-sm border-t-4 ${m.color} overflow-hidden`}>
                        <CardContent className="p-5">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{m.label}</p>
                            <p className={`text-2xl font-extrabold ${m.textColor} mt-2 tabular-nums`} style={{ fontFamily: 'Chivo, sans-serif' }}>{m.value}</p>
                            <p className="text-xs text-slate-500 mt-1">Avg progress {avgProgress}%</p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Goals list */}
            <div className="space-y-3">
                {loading && <p className="text-slate-500 text-sm">Loading goals...</p>}
                {!loading && goals.length === 0 && (
                    <Card className="rounded-xl shadow-sm"><CardContent className="p-12 text-center"><Target className="h-12 w-12 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No goals yet — set your first quarterly objective above.</p></CardContent></Card>
                )}
                {goals.map(g => (
                    <Card key={g._id} className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-testid={`goal-card-${g._id}`}>
                        <CardContent className="p-5">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLORS[g.status] || 'bg-slate-100 text-slate-600'}`}>{(g.status || 'not_started').replace(/_/g, ' ')}</span>
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{g.quarter}</span>
                                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{g.category}</span>
                                    </div>
                                    <h3 className="font-bold text-slate-900 text-base">{g.title}</h3>
                                    {g.description && <p className="text-sm text-slate-500 mt-1">{g.description}</p>}
                                    {g.key_results && g.key_results.length > 0 && (
                                        <ul className="mt-3 space-y-1">
                                            {g.key_results.map(kr => (
                                                <li key={kr.id} className="text-xs text-slate-600 flex items-center gap-2">
                                                    <CheckCircle2 className={`h-3 w-3 ${kr.actual >= kr.target ? 'text-emerald-500' : 'text-slate-300'}`} />
                                                    {kr.title} <span className="font-mono text-slate-500">· {kr.actual}/{kr.target} {kr.unit}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <div className="text-right">
                                        <p className="text-3xl font-extrabold text-slate-900 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{g.progress_pct || 0}%</p>
                                        <p className="text-[10px] text-slate-400">{g.checkins?.length || 0} check-in{g.checkins?.length === 1 ? '' : 's'}</p>
                                    </div>
                                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => { setCheckinOpen(g._id); setCheckinForm({ progress_pct: g.progress_pct || 0, note: '', confidence: 'on_track' }); }} data-testid={`goal-checkin-${g._id}`}>
                                        <TrendingUp className="h-3.5 w-3.5 mr-1" />Check-in
                                    </Button>
                                </div>
                            </div>
                            <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${g.status === 'completed' ? 'bg-emerald-500' : g.status === 'at_risk' ? 'bg-amber-500' : 'bg-[#2563eb]'}`} style={{ width: `${g.progress_pct || 0}%` }} />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Check-in dialog */}
            <Dialog open={!!checkinOpen} onOpenChange={(o) => !o && setCheckinOpen(null)}>
                <DialogContent className="rounded-2xl max-w-md">
                    <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Weekly Check-in</DialogTitle><DialogDescription>Update your goal progress + share blockers.</DialogDescription></DialogHeader>
                    <div className="space-y-3 mt-3">
                        <div><Label className="text-xs font-bold uppercase text-slate-500">Progress %</Label>
                            <input type="range" min="0" max="100" value={checkinForm.progress_pct} onChange={e => setCheckinForm({ ...checkinForm, progress_pct: parseInt(e.target.value) })} className="w-full mt-2" />
                            <p className="text-2xl font-bold text-center mt-1 tabular-nums">{checkinForm.progress_pct}%</p>
                        </div>
                        <div><Label className="text-xs font-bold uppercase text-slate-500">Confidence</Label>
                            <select value={checkinForm.confidence} onChange={e => setCheckinForm({ ...checkinForm, confidence: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm mt-1">
                                <option value="on_track">On track ✅</option>
                                <option value="at_risk">At risk ⚠️</option>
                                <option value="off_track">Off track 🚨</option>
                            </select>
                        </div>
                        <div><Label className="text-xs font-bold uppercase text-slate-500">Note / Blockers</Label><Textarea value={checkinForm.note} onChange={e => setCheckinForm({ ...checkinForm, note: e.target.value })} rows={2} className="rounded-lg mt-1 bg-slate-50 border-slate-200" /></div>
                        <div className="flex justify-end gap-2"><Button variant="outline" className="rounded-lg" onClick={() => setCheckinOpen(null)}>Cancel</Button><Button onClick={submitCheckin} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm">Submit Check-in</Button></div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Goals;
